// Teaching notation only. scratch/car/controller/robot APIs below are illustrative, not a runtime.
const literal = value => {
    if (value === null || typeof value === 'undefined') return 'None';
    if (typeof value === 'boolean') return value ? 'True' : 'False';
    if (typeof value === 'number') return Number.isFinite(value) ? String(value) : 'None';
    return JSON.stringify(String(value));
};

const calls = {
    control_wait: ['scratch.wait', 'DURATION'],
    control_stop: ['scratch.stop', 'STOP_OPTION'],
    event_broadcast: ['scratch.broadcast', 'BROADCAST_INPUT'],
    event_broadcastandwait: ['scratch.broadcast_and_wait', 'BROADCAST_INPUT'],
    looks_say: ['scratch.say', 'MESSAGE'],
    looks_sayforsecs: ['scratch.say_for_seconds', 'MESSAGE', 'SECS'],
    looks_think: ['scratch.think', 'MESSAGE'],
    motion_movesteps: ['scratch.move_steps', 'STEPS'],
    motion_turnright: ['scratch.turn_right', 'DEGREES'],
    motion_turnleft: ['scratch.turn_left', 'DEGREES'],
    motion_gotoxy: ['scratch.go_to', 'X', 'Y'],
    sound_play: ['scratch.play_sound', 'SOUND_MENU'],
    sound_playuntildone: ['scratch.play_sound_until_done', 'SOUND_MENU'],
    domiCar_connect: ['car.connect', 'ADDRESS'],
    domiCar_move: ['car.move', 'DIRECTION'],
    domiCar_moveForSeconds: ['car.move_for_seconds', 'DIRECTION', 'SECONDS'],
    domiCar_holdKeyMove: ['car.move_while_key_held', 'KEY', 'DIRECTION'],
    domiCar_stop: ['car.stop'],
    domiCar_setLight: ['car.set_light', 'STATE'],
    domiCar_toggleLight: ['car.toggle_light'],
    domiCar_openCameraView: ['car.open_camera'],
    domiCar_closeCameraView: ['car.close_camera'],
    domiRobotDog_connect: ['robot.connect', 'ADDRESS'],
    domiRobotDog_move: ['robot.move', 'MOVEMENT'],
    domiRobotDog_standby: ['robot.standby'],
    domiRobotDog_sayHello: ['robot.say_hello'],
    domiRobotDog_fight: ['robot.fight'],
    domiRobotDog_pushUp: ['robot.push_up'],
    domiRobotDog_sleep: ['robot.sleep'],
    domiRobotDog_perform: ['robot.perform', 'ACTION']
};

const events = {
    event_whenflagclicked: ['scratch.on_green_flag'],
    event_whenkeypressed: ['scratch.on_key_pressed', 'KEY_OPTION'],
    event_whenthisspriteclicked: ['scratch.on_sprite_clicked'],
    event_whenstageclicked: ['scratch.on_stage_clicked'],
    event_whenbroadcastreceived: ['scratch.on_broadcast', 'BROADCAST_OPTION'],
    control_start_as_clone: ['scratch.on_clone_start'],
    domiCar_whenCarStarts: ['car.on_start'],
    domiController_whenButtonAction: ['controller.on_button', 'BUTTON', 'ACTION']
};

const reporters = {
    operator_add: ['scratch.add', 'NUM1', 'NUM2'],
    operator_subtract: ['scratch.subtract', 'NUM1', 'NUM2'],
    operator_multiply: ['scratch.multiply', 'NUM1', 'NUM2'],
    operator_divide: ['scratch.divide', 'NUM1', 'NUM2'],
    operator_equals: ['scratch.equals', 'OPERAND1', 'OPERAND2'],
    operator_gt: ['scratch.greater_than', 'OPERAND1', 'OPERAND2'],
    operator_lt: ['scratch.less_than', 'OPERAND1', 'OPERAND2'],
    operator_random: ['scratch.random', 'FROM', 'TO'],
    operator_join: ['scratch.join', 'STRING1', 'STRING2'],
    operator_mod: ['scratch.mod', 'NUM1', 'NUM2'],
    operator_round: ['scratch.round', 'NUM'],
    operator_mathop: ['scratch.math', 'OPERATOR', 'NUM'],
    sensing_keypressed: ['scratch.key_pressed', 'KEY_OPTION'],
    sensing_timer: ['scratch.timer'],
    sensing_answer: ['scratch.answer'],
    domiCar_isConnected: ['car.is_connected'],
    domiRobotDog_isConnected: ['robot.is_connected']
};

const projectToPython = project => {
    const warnings = new Set();
    const lines = [
        '# Python teaching view — NOT a standalone executable program.',
        '# scratch/car/controller/robot are illustrative APIs; no Python runtime is included.',
        '# Events are independent handlers. Scratch scheduling and type conversion require a runtime.',
        '# Variable IDs preserve sprite/stage scope. Unsupported blocks are explicitly marked.',
        ''
    ];
    const targets = project.targets || [project];
    let budget = 20000;
    targets.forEach((target, targetIndex) => {
        const blocks = target.blocks || {};
        const procedures = new Map();
        const roots = Object.keys(blocks).filter(id => blocks[id].topLevel && !blocks[id].shadow);
        roots.forEach(id => {
            const block = blocks[id];
            if (block.opcode !== 'procedures_definition') return;
            const prototype = blocks[block.inputs.custom_block[1]];
            const mutation = prototype.mutation;
            procedures.set(mutation.proccode, {
                name: `procedure_${procedures.size + 1}`,
                ids: JSON.parse(mutation.argumentids || '[]'),
                names: JSON.parse(mutation.argumentnames || '[]'),
                warp: mutation.warp === true || mutation.warp === 'true'
            });
        });
        let argumentsByName = new Map();
        const warn = (opcode, id) => {
            warnings.add(`${target.name}: ${opcode} (${id})`);
            return `unsupported_block(${literal(opcode)}, ${literal(id)})`;
        };
        const variable = (field, method = 'get_variable', value) =>
            `scratch.${method}(${literal(field[0])}, ${literal(field[1])}${value ? `, ${value}` : ''})`;
        const input = (block, key, path = new Set()) => {
            const entry = (block.inputs || {})[key];
            if (!entry) return literal(((block.fields || {})[key] || [null])[0]);
            const value = entry[1] === null ? entry[2] : entry[1];
            if (Array.isArray(value)) {
                if (value[0] === 12) return variable(value.slice(1));
                if (value[0] === 13) return variable(value.slice(1), 'get_list');
                if (value[0] >= 4 && value[0] <= 8) return literal(Number(value[1]));
                return literal(value[1]);
            }
            return expression(value, path);
        };
        const call = (spec, block, path) =>
            `${spec[0]}(${spec.slice(1).map(key => input(block, key, path))
                .join(', ')})`;
        const expression = (id, path = new Set()) => {
            if (!id) return 'None';
            if (path.has(id) || path.size > 80 || --budget < 0) return warn('invalid_or_too_large_graph', id);
            const block = blocks[id];
            if (!block) return warn('missing_block', id);
            const nextPath = new Set([...path, id]);
            const op = block.opcode;
            if (reporters[op]) return call(reporters[op], block, nextPath);
            if (op === 'data_variable') return variable(block.fields.VARIABLE);
            if (op === 'argument_reporter_string_number' || op === 'argument_reporter_boolean') {
                return argumentsByName.get(block.fields.VALUE[0]) || warn(op, id);
            }
            if (op === 'operator_and' || op === 'operator_or') {
                return `(scratch.to_bool(${input(block, 'OPERAND1', nextPath)}) ` +
                    `${op === 'operator_and' ? 'and' : 'or'} ` +
                    `scratch.to_bool(${input(block, 'OPERAND2', nextPath)}))`;
            }
            if (op === 'operator_not') return `(not scratch.to_bool(${input(block, 'OPERAND', nextPath)}))`;
            if (/^(domiCar|domiRobotDog)_menu_/.test(op) ||
                ['sensing_keyoptions', 'sound_sounds_menu'].includes(op)) {
                return literal(Object.values(block.fields)[0][0]);
            }
            return warn(op, id);
        };
        const emit = (depth, text) => lines.push(`${'    '.repeat(depth)}${text}`);
        const stack = (start, depth, ancestors = new Set()) => {
            let id = start;
            const path = new Set(ancestors);
            if (!id) emit(depth, 'pass');
            while (id) {
                if (path.has(id) || depth > 80 || --budget < 0) {
                    emit(depth, warn('invalid_or_too_large_graph', id));
                    break;
                }
                path.add(id);
                const block = blocks[id];
                if (!block) {
                    emit(depth, warn('missing_block', id));
                    break;
                }
                const op = block.opcode;
                const arg = key => input(block, key);
                const body = key => stack(((block.inputs || {})[key] || [])[1], depth + 1, path);
                if (calls[op]) emit(depth, call(calls[op], block));
                else if (op === 'data_setvariableto' || op === 'data_changevariableby') {
                    emit(depth, variable(block.fields.VARIABLE,
                        op === 'data_setvariableto' ? 'set_variable' : 'change_variable', arg('VALUE')));
                } else if (op === 'control_repeat') {
                    emit(depth, `for _ in range(scratch.repeat_count(${arg('TIMES')})):`);
                    body('SUBSTACK');
                } else if (op === 'control_forever' || op === 'control_repeat_until') {
                    emit(depth, op === 'control_forever' ? 'while True:' :
                        `while not scratch.to_bool(${arg('CONDITION')}):`);
                    body('SUBSTACK');
                } else if (op === 'control_if' || op === 'control_if_else') {
                    emit(depth, `if scratch.to_bool(${arg('CONDITION')}):`);
                    body('SUBSTACK');
                    if (op === 'control_if_else') {
                        emit(depth, 'else:');
                        body('SUBSTACK2');
                    }
                } else if (op === 'control_wait_until') {
                    emit(depth, `while not scratch.to_bool(${arg('CONDITION')}):`);
                    emit(depth + 1, 'scratch.yield_tick()');
                } else if (op === 'procedures_call' && procedures.has(block.mutation.proccode)) {
                    const proc = procedures.get(block.mutation.proccode);
                    emit(depth, `self.${proc.name}(${proc.ids.map(arg).join(', ')})`);
                } else emit(depth, expression(id));
                id = block.next;
            }
        };
        lines.push(`# Target: ${literal(target.name)}`, `class Target_${targetIndex + 1}:`);
        Object.entries(target.variables || {}).forEach(([id, data]) => {
            emit(1, `# Variable ${literal(data[0])} [${literal(id)}], saved value: ${literal(data[1])}`);
        });
        if (!roots.length) emit(1, 'pass');
        roots.forEach((id, index) => {
            const block = blocks[id];
            argumentsByName = new Map();
            if (block.opcode === 'procedures_definition') {
                const prototype = blocks[block.inputs.custom_block[1]];
                const proc = procedures.get(prototype.mutation.proccode);
                proc.names.forEach((name, i) => argumentsByName.set(name, `arg_${i + 1}`));
                emit(1, `# Custom block: ${literal(prototype.mutation.proccode)}`);
                if (proc.warp) emit(1, '@scratch.without_screen_refresh');
                emit(1, `def ${proc.name}(self${proc.ids.map((_, i) => `, arg_${i + 1}`).join('')}):`);
                stack(block.next, 2);
            } else if (events[block.opcode]) {
                emit(1, `@${call(events[block.opcode], block)}`);
                emit(1, `def event_${index + 1}(self):`);
                stack(block.next, 2);
            } else {
                emit(1, '# Detached stack: runs only when manually triggered.');
                emit(1, `def manual_${index + 1}(self):`);
                stack(id, 2);
            }
            lines.push('');
        });
        lines.push('');
    });
    if (warnings.size) {
        lines.push('# Unsupported blocks / conversion warnings:');
        warnings.forEach(warning => lines.push(`# ${literal(warning)}`));
    }
    return {text: `${lines.join('\n')}\n`, warnings: warnings.size};
};

const getProjectPython = (vm, scope) => {
    if (scope === 'target' && !vm.editingTarget) return {text: '', name: '', warnings: 0};
    const project = JSON.parse(scope === 'target' ? vm.toJSON(vm.editingTarget.id) : vm.toJSON());
    return {...projectToPython(project), name: scope === 'target' ? project.name : ''};
};

export {getProjectPython, projectToPython};
